import type { FormEvent, ReactNode, RefObject } from "react";
import { useTranslation } from "react-i18next";

import type { FormState } from "./state";
import { TurnstileWidget } from "./TurnstileWidget";

interface FormViewProps {
	formRef: RefObject<HTMLFormElement>;
	nameRef: RefObject<HTMLInputElement>;
	state: FormState;
	open: boolean;
	sitekey: string | undefined;
	turnstileMessage: string | null;
	submitDisabled: boolean;
	onSubmit: (e: FormEvent<HTMLFormElement>) => void;
	onFieldChange: () => void;
	onToken: (token: string) => void;
	onTurnstileError: () => void;
}

export function FormView({
	formRef,
	nameRef,
	state,
	open,
	sitekey,
	turnstileMessage,
	submitDisabled,
	onSubmit,
	onFieldChange,
	onToken,
	onTurnstileError,
}: FormViewProps): ReactNode {
	const { t } = useTranslation();

	return (
		<>
			<h2>{t("cta.modalTitle")}</h2>
			<form ref={formRef} className="ea-form" onSubmit={onSubmit}>
				<div className="ea-row">
					<div className="ea-field">
						<label htmlFor="ea-name">{t("cta.nameLabel")}</label>
						<input
							ref={nameRef}
							id="ea-name"
							name="name"
							type="text"
							required
							placeholder={t("cta.namePlaceholder")}
							onChange={onFieldChange}
						/>
						{state.fieldErrors.name && (
							<span className="ea-field-error">{state.fieldErrors.name}</span>
						)}
					</div>
					<div className="ea-field">
						<label htmlFor="ea-email">{t("cta.emailLabel")}</label>
						<input
							id="ea-email"
							name="email"
							type="email"
							required
							placeholder={t("cta.emailPlaceholder")}
							onChange={onFieldChange}
						/>
						{state.fieldErrors.email && (
							<span className="ea-field-error">{state.fieldErrors.email}</span>
						)}
					</div>
				</div>

				<div className="ea-field">
					<label>{t("cta.typeLabel")}</label>
					<div className="ea-radio-group">
						<label className="ea-radio">
							<input
								type="radio"
								name="type"
								value="person"
								defaultChecked
								onChange={onFieldChange}
							/>
							<span>{t("cta.typePerson")}</span>
						</label>
						<label className="ea-radio">
							<input type="radio" name="type" value="company" onChange={onFieldChange} />
							<span>{t("cta.typeCompany")}</span>
						</label>
					</div>
				</div>

				<div className="ea-field">
					<label htmlFor="ea-desc">{t("cta.descriptionLabel")}</label>
					<textarea
						id="ea-desc"
						name="description"
						rows={3}
						maxLength={1000}
						placeholder={t("cta.descriptionPlaceholder")}
						onChange={onFieldChange}
					/>
					{state.fieldErrors.description && (
						<span className="ea-field-error">{state.fieldErrors.description}</span>
					)}
				</div>

				<label className="ea-checkbox">
					<input type="checkbox" name="earlyAccessEmail" onChange={onFieldChange} />
					<span>{t("cta.earlyAccessEmailLabel")}</span>
				</label>
				{state.fieldErrors.earlyAccessEmail && (
					<span className="ea-field-error">{state.fieldErrors.earlyAccessEmail}</span>
				)}

				<label className="ea-checkbox">
					<input type="checkbox" name="updates" onChange={onFieldChange} />
					<span>{t("cta.updatesLabel")}</span>
				</label>

				<p className="ea-no-spam">{t("cta.noSpamNote")}</p>

				<div className="ea-turnstile">
					{open && state.turnstileStatus === "ready" && sitekey && (
						<TurnstileWidget
							key={state.widgetKey}
							sitekey={sitekey}
							onToken={onToken}
							onError={onTurnstileError}
						/>
					)}
					{turnstileMessage && (
						<p
							className={
								state.turnstileStatus === "loading" ? "ea-turnstile-status" : "ea-field-error"
							}
						>
							{turnstileMessage}
						</p>
					)}
					{state.fieldErrors.turnstileToken && (
						<span className="ea-field-error">{state.fieldErrors.turnstileToken}</span>
					)}
				</div>

				{state.status === "error" && (
					<div className="ea-error" role="alert">
						{state.errorMsg}
					</div>
				)}

				<button className="btn btn-primary ea-submit" type="submit" disabled={submitDisabled}>
					{state.status === "submitting" ? t("cta.submitting") : t("cta.submit")}{" "}
					<span className="arrow">&rarr;</span>
				</button>
			</form>
		</>
	);
}
